import {testIdProps} from '@github-ui/test-id-props'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {BaseStyles, useTheme} from '@primer/styled-react'
import {clsx} from 'clsx'
import {
  forwardRef,
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import {useSliceBy} from '../../features/slicing/hooks/use-slice-by'
import {useSubIssuesLoading} from '../../features/sub-issues/use-sub-issues-loading'
import {resetScrollPositionImmediately} from '../../helpers/scroll-utilities'
import {addNoGroupWhenGroupsDisabled} from '../../helpers/table-group-utilities'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import type {TableInstance} from '../../hooks/tanstack-table/helpers/types'
import {useColumnWidthVariables} from '../../hooks/tanstack-table/hooks/use-column-width-variables'
import {AddColumnHeaderColumnDef} from '../../hooks/tanstack-table/providers/columns/add-column-header-column-def'
import {TableProvider, useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import {useReactTable} from '../../hooks/tanstack-table/use-react-table'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useInitialTableFocusAction} from '../../hooks/use-initial-table-focus-action'
import {useMeasureScrollbars} from '../../hooks/use-measure-scrollbars'
import {LoadingStates, useViewLoadingState} from '../../hooks/use-view-loading-state'
import {useViews} from '../../hooks/use-views'
import {useVisibleFields} from '../../hooks/use-visible-fields'
import {useBulkAddNotifications} from '../../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {usePaginatedMemexItemsQuery} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {usePaginatedTotalCount} from '../../state-providers/memex-items/queries/use-paginated-total-count'
import {BulkAddNotifications} from '../bulk-add-notifications'
import {MoveDialog, MoveDialogContext, type MoveDialogProps} from '../move-item/move-dialog/move-dialog'
import {OmnibarContainer} from '../omnibar/omnibar-container'
import {SlicerItemsProvider, useSlicerItems} from '../slicer-panel/slicer-items-provider'
import {SlicerPanel} from '../slicer-panel/slicer-panel'
import {TableDragToFillProvider} from './bulk-fill-values'
import {CELL_HEIGHT, TABLE_HEADER_HEIGHT} from './constants'
import {CopilotAssignmentModalProvider} from './copilot-assignment-modal-provider'
import {CopilotAssignmentModalRenderer} from './copilot-assignment-modal-renderer'
import {CopyPasteProvider} from './hooks/use-copy-paste'
import {useIsOmnibarFixed} from './hooks/use-is-omnibar-fixed'
import {useScrollToNewItem} from './hooks/use-scroll-to-new-item'
import {focusSearchInput, type TableFocusMeta, TableNavigationProvider, useTableNavigation} from './navigation'
import {useRecordComponent} from './performance-measurements'
import styles from './table.module.css'
import {TableBodyWithObserver} from './table-body-with-observer'
import {TableCellBulkSelectionProvider} from './table-cell-bulk-selection'
import type {TableDataType} from './table-data-type'
import {TableFilterInput} from './table-filter-input'
import {TableOmnibar} from './table-omnibar'
import {PLACEHOLDER_ROW_COUNT} from './table-pagination'
import {useItemData} from './use-item-data'
import {useSelectionBlur} from './use-selection-blur'
import {useTableFocus} from './use-table-focus'

interface TableRef {
  focusIn: () => void
}

const getHeaderFocus = () => focusSearchInput().focus

export const Table = memo(
  forwardRef<TableRef>(function Table(_, forwardedRef) {
    const {visibleFields} = useVisibleFields()
    const tableProps = useMemo(
      () => ({fields: visibleFields, pinnedRightColumnDefs: [AddColumnHeaderColumnDef]}),
      [visibleFields],
    )
    const table = useReactTable(tableProps)
    return (
      <TableProvider table={table} cellHeight={CELL_HEIGHT}>
        <SlicerItemsProvider>
          <TableLayout ref={forwardedRef} />
        </SlicerItemsProvider>
      </TableProvider>
    )
  }),
)

const TableLayout = forwardRef<TableRef>(function TableLayout(_, forwardedRef) {
  useRecordComponent('Table', 'Table', '')
  const {sliceField} = useSliceBy()
  const {slicerItems} = useSlicerItems()
  const tableInstance = useTableInstance()

  const metaRef = useRef<TableFocusMeta>({tableInstance, getHeaderFocus})

  const totalCount = usePaginatedTotalCount()

  return (
    <CopilotAssignmentModalProvider>
      <TableNavigationProvider metaRef={metaRef}>
        <TableCellBulkSelectionProvider>
          <TableDragToFillProvider>
            <CopyPasteProvider>
              {sliceField && <SlicerPanel slicerItems={slicerItems} />}
              <TableFilterInput filterCount={useDeferredValue(totalCount)} />
              <BulkAddNotifications />
              <TableInner table={tableInstance} ref={forwardedRef} />
              <CopilotAssignmentModalRenderer />
            </CopyPasteProvider>
          </TableDragToFillProvider>
        </TableCellBulkSelectionProvider>
      </TableNavigationProvider>
    </CopilotAssignmentModalProvider>
  )
})

type InnerTableProps = {
  table: TableInstance<TableDataType>
}

const TableInner = forwardRef<TableRef, InnerTableProps>(function TableInner({table}, forwardedRef) {
  const {onKeyDown, onBlur} = useTableFocus()
  const {navigationDispatch} = useTableNavigation()
  const [moveDialogProps, setMoveDialogProps] = useState<MoveDialogProps | null>(null)
  const {shouldShowSkeleton} = useBulkAddNotifications()
  const {memex_disable_autofocus, memex_small_viewport_a11y} = useEnabledFeatures()

  const initialFocusAction = useInitialTableFocusAction(table)
  const focusIn = useCallback(() => {
    if (initialFocusAction) navigationDispatch(initialFocusAction)
  }, [navigationDispatch, initialFocusAction])

  const columnWidthVariables = useColumnWidthVariables(table.allColumns)

  // focus in on init
  useEffect(() => {
    if (!memex_disable_autofocus) focusIn()
  }, [focusIn, memex_disable_autofocus])

  useImperativeHandle(forwardedRef, () => ({focusIn}))

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const {hasWritePermissions} = ViewerPrivileges()
  const {loadingState} = useViewLoadingState()
  const {hasNextPage} = usePaginatedMemexItemsQuery()
  const {getCountLoadingSubIssues, getCountMissingSubIssues} = useSubIssuesLoading()

  const placeholderRowCount = hasNextPage || shouldShowSkeleton ? PLACEHOLDER_ROW_COUNT : 0
  const subIssuePlaceholderRowCount = getCountLoadingSubIssues()
  const missingSubIssuesRowCount = getCountMissingSubIssues(table.rows)
  const additionalRows = placeholderRowCount + subIssuePlaceholderRowCount + missingSubIssuesRowCount
  const effectiveTableHeight =
    loadingState === LoadingStates.loaded
      ? (table.rows.length + additionalRows) * CELL_HEIGHT + TABLE_HEADER_HEIGHT
      : CELL_HEIGHT + TABLE_HEADER_HEIGHT
  const isOmnibarFixed = useIsOmnibarFixed({
    isGrouped: table.groupedRows !== undefined,
    totalHeight: effectiveTableHeight,
    scrollRef,
  })
  const {horizontalScrollbarSize, verticalScrollbarSize} = useMeasureScrollbars(scrollRef)

  // When a new item is added, wait for paint and scroll the table to the new
  // row (the bottom).
  const {onNewItem} = useScrollToNewItem(scrollRef, table.rows, {left: 0, rowHeight: CELL_HEIGHT})

  const {currentView} = useViews()
  const currentViewNumber = currentView?.number

  const {containerRef} = useSelectionBlur()

  const itemData = useItemData(table)

  useLayoutEffect(() => {
    resetScrollPositionImmediately(scrollRef.current)
  }, [currentViewNumber])

  const tableGroups = table?.groupedRows ? addNoGroupWhenGroupsDisabled(table.groupedRows) : undefined
  const {theme} = useTheme()

  return (
    <>
      <MoveDialogContext.Provider value={useMemo(() => ({setMoveDialogProps}), [setMoveDialogProps])}>
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
        <div
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          role="grid"
          {...testIdProps('table-root')}
          style={{
            ...columnWidthVariables,
            backgroundImage: table.groupedRows
              ? 'none'
              : `url("data:image/svg+xml,%3Csvg viewBox='0 0 1 1' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='1' height='1' fill='transparent' /%3E%3C/svg%3E"), url("data:image/svg+xml,%3Csvg viewBox='0 0 1 1' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='1' height='1' fill='${encodeURIComponent(
                  theme?.colors.border.muted as string,
                )}' /%3E%3C/svg%3E")`,
          }}
          className={clsx(styles.tableRoot, !table.groupedRows && isOmnibarFixed && styles.fixedOmnibar)}
        >
          {moveDialogProps && (
            <MoveDialog
              close={() => {
                setMoveDialogProps(null)
              }}
              {...moveDialogProps}
            />
          )}
          <div
            data-memex-small-viewport-a11y={memex_small_viewport_a11y}
            className={styles.tableBodyContainer}
            data-hpc
          >
            <BaseStyles style={{fontSize: '14px'}}>
              <TableBodyWithObserver
                scrollRef={scrollRef}
                containerRef={containerRef}
                table={table}
                tableGroups={tableGroups}
                isOmnibarFixed={isOmnibarFixed}
                loadingState={loadingState}
                hasWritePermissions={hasWritePermissions}
                itemData={itemData}
              />
            </BaseStyles>
          </div>
          {(!tableGroups || tableGroups.length === 0) && hasWritePermissions && (
            <OmnibarContainer
              horizontalScrollbarSize={horizontalScrollbarSize}
              verticalScrollbarSize={verticalScrollbarSize}
              isFixed={isOmnibarFixed}
              effectiveTableHeight={effectiveTableHeight}
            >
              <TableOmnibar onAddItem={onNewItem} isFixed={isOmnibarFixed} />
            </OmnibarContainer>
          )}
        </div>
      </MoveDialogContext.Provider>
    </>
  )
})

try{ Table.displayName ||= 'Table' } catch {}
try{ Table.displayName ||= 'Table' } catch {}
try{ TableLayout.displayName ||= 'TableLayout' } catch {}
try{ TableInner.displayName ||= 'TableInner' } catch {}